// src/utils/mockDataBudget.ts
// Step 1 of incremental procurement build — budget enforcement gate
// Adds configurable thresholds and real-time budget checks to PO creation

// ── Types ─────────────────────────────────────────────────────────

export type BudgetStatus = 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'BLOCKED'

export interface BudgetConfig {
  jobId: string
  warnPct: number       // amber warning threshold — default 80%
  escalatePct: number   // PM comment/override required — default 95%
  blockPct: number      // hard stop — default 100%
}

export interface BudgetCheckResult {
  status: BudgetStatus
  currentPct: number
  projectedPct: number
  currentCommitted: number
  projectedTotal: number
  contractValue: number
  remaining: number
  projectedRemaining: number
  message: string
  canProceed: boolean
  requiresPMOverride: boolean
}

// ── Per-job budget configuration ──────────────────────────────────
// Falls back to defaults for any job not listed here.

export const DEFAULT_THRESHOLDS = { warnPct: 80, escalatePct: 95, blockPct: 100 }

export const JOB_BUDGET_CONFIGS: Record<string, BudgetConfig> = {
  j1: { jobId: 'j1', warnPct: 80, escalatePct: 95, blockPct: 100 },
  j2: { jobId: 'j2', warnPct: 75, escalatePct: 90, blockPct: 100 },
  j3: { jobId: 'j3', warnPct: 80, escalatePct: 95, blockPct: 100 },
  j4: { jobId: 'j4', warnPct: 70, escalatePct: 90, blockPct: 100 },
}

export function getBudgetConfig(jobId: string): BudgetConfig {
  return JOB_BUDGET_CONFIGS[jobId] ?? { jobId, ...DEFAULT_THRESHOLDS }
}

// ── Budget status metadata ─────────────────────────────────────────

export const BUDGET_STATUS_META: Record<BudgetStatus, {
  label: string; bg: string; text: string; border: string; barColor: string
}> = {
  HEALTHY:  { label: 'Healthy',    bg: 'bg-green-50',  text: 'text-green-800',  border: 'border-green-200',  barColor: 'bg-green-500'  },
  WARNING:  { label: 'Warning',    bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200',  barColor: 'bg-amber-400'  },
  CRITICAL: { label: 'Critical',   bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300', barColor: 'bg-orange-500' },
  BLOCKED:  { label: 'Over limit', bg: 'bg-red-50',    text: 'text-red-800',    border: 'border-red-300',    barColor: 'bg-red-500'    },
}

// ── Core check function ───────────────────────────────────────────

export function checkBudget(
  contractValue: number,
  openCommitted: number,
  newAmount: number,
  jobId: string
): BudgetCheckResult {
  const cfg = getBudgetConfig(jobId)
  const currentPct     = contractValue > 0 ? (openCommitted / contractValue) * 100 : 0
  const projectedTotal = openCommitted + newAmount
  const projectedPct   = contractValue > 0 ? (projectedTotal / contractValue) * 100 : 0
  const remaining          = contractValue - openCommitted
  const projectedRemaining = contractValue - projectedTotal

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n)

  let status: BudgetStatus
  let message: string
  let canProceed: boolean
  let requiresPMOverride: boolean

  if (projectedPct >= cfg.blockPct) {
    status             = 'BLOCKED'
    canProceed         = false
    requiresPMOverride = true
    message = `This PO would commit ${projectedPct.toFixed(1)}% of the contract — exceeding the ${cfg.blockPct}% hard limit. PM authorisation required to override.`
  } else if (projectedPct >= cfg.escalatePct) {
    status             = 'CRITICAL'
    canProceed         = true
    requiresPMOverride = true
    message = `Commitment will reach ${projectedPct.toFixed(1)}% after this PO. Only ${fmt(projectedRemaining)} remaining. PM must review and sign off.`
  } else if (projectedPct >= cfg.warnPct) {
    status             = 'WARNING'
    canProceed         = true
    requiresPMOverride = false
    message = `Commitment will reach ${projectedPct.toFixed(1)}% after this PO (${fmt(projectedRemaining)} remaining). Proceed with PM awareness.`
  } else {
    status             = 'HEALTHY'
    canProceed         = true
    requiresPMOverride = false
    message = `Budget healthy — ${fmt(projectedRemaining)} will remain after this PO.`
  }

  return {
    status, currentPct, projectedPct,
    currentCommitted: openCommitted, projectedTotal,
    contractValue, remaining, projectedRemaining,
    message, canProceed, requiresPMOverride,
  }
}
